/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation
} from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { MatchMediaService } from '../../services/match-media.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { catchError, debounceTime, take, takeUntil } from 'rxjs/operators';
import { LeftNavigationMenu, NavigationMenuItem } from '../../models/navigation-menu-item';
import { of } from 'rxjs';

@Component({
  selector: 'fc-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SidebarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly LOCAL_STORAGE_SIDEBAR_STATE_KEY = 'sidebarState';
  private _shouldRedirect = true;
  private _folded: boolean;
  private _wasActive!: boolean;
  private _wasFolded!: boolean;

  @Input() foldedWidth = 40;
  @Input() name!: string;
  @Input() lockedOpen!: string;

  @Input()
  set folded(value: boolean) {
    this._folded = value;

    if (!this.opened) {
      return;
    }

    let sibling = this._elementRef.nativeElement.nextElementSibling;

    // If there is no sibling, return...
    if (!sibling) {
      return;
    }

    if (!value) {
      this.unfold();

      this._renderer.removeStyle(this._elementRef.nativeElement, 'width');
      this._renderer.removeStyle(this._elementRef.nativeElement, 'min-width');
      this._renderer.removeStyle(this._elementRef.nativeElement, 'max-width');

      this._renderer.removeClass(this._elementRef.nativeElement, 'folded');
    }
  }

  get folded(): boolean {
    return this._folded;
  }

  @Output() temporaryUnfold = new EventEmitter();
  @Output() temporaryFold = new EventEmitter();

  sidebarError = false;
  isLoading = true;
  pinned = false;
  isHover: boolean;
  sidebarStateFlag = false;

  @HostBinding('class.open')
  opened: boolean;

  @HostBinding('class.locked-open')
  isLockedOpen!: boolean;

  @HostBinding('class.unfolded')
  unfolded!: boolean;

  @HostBinding('class.fc-sidebar-overlay')
  sidebarOverlay!: boolean;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _elementRef: ElementRef,
    private _matchMediaService: MatchMediaService,
    private _renderer: Renderer2,
    public sidebarService: SidebarService,
    private router: Router
  ) {
    this.opened = false;
    this._folded = false;
    this.isHover = false;
  }

  /**
   * On init
   */
  ngOnInit(): void {
    this._getMenuItems();

    this.sidebarService.menuShouldBeFolded$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((sideBarFoldedState) => {
        sideBarFoldedState == true ? this.foldTemporarily() : this.unfoldTemporarily();
      });

    const windowInnerWidth = window.innerWidth;
    const sidebarState = localStorage.getItem(this.LOCAL_STORAGE_SIDEBAR_STATE_KEY);

    if (sidebarState && JSON.parse(sidebarState)) {
      this.sidebarStateFlag = true;
      this.setupSidebarByState();
    } else if (windowInnerWidth >= 1900) {
      //The large resolution should have left nav panel opened by default
      this.sidebarStateFlag = true;
      this.setupSidebarByState();
      localStorage.removeItem(this.LOCAL_STORAGE_SIDEBAR_STATE_KEY);
    } else {
      // Setup visibility
      this._setupVisibility();

      // Setup position
      this._setupPosition();

      // Setup lockedOpen
      this._setupLockedOpen();

      // Setup folded
      this._setupFolded();
    }

    // set width according to current session
    this.setSidebarWidthAccordingToSessionStorage();
  }

  setSidebarWidthAccordingToSessionStorage() {
    if (localStorage.getItem('sidebarWidth')) {
      const sidebarWidth = localStorage.getItem('sidebarWidth');
      this._renderer.setStyle(
        this._elementRef.nativeElement,
        'width',
        sidebarWidth
      );
    }
  }

  setupSidebarByState(): void {
    this.onPinClick();
    this._setupLockedOpen();
    this.sidebarService.setFoldedState(this.folded);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // If the sidebar is folded, unfold it to revert modifications
    if (this.folded) {
      this.unfold();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setup the visibility of the sidebar
   *
   * @private
   */
  private _setupVisibility(): void {
    // Remove the existing box-shadow
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'box-shadow',
      'none'
    );

    // Make the sidebar invisible
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'visibility',
      'hidden'
    );
  }

  /**
   * Setup the sidebar position
   *
   * @private
   */
  private _setupPosition(): void {
    this._renderer.addClass(this._elementRef.nativeElement, 'left-positioned');
  }

  /**
   * Setup the lockedOpen handler
   *
   * @private
   */
  private _setupLockedOpen(): void {
    // Return if the lockedOpen wasn't set
    if (!this.lockedOpen) {
      // Return
      return;
    }

    // Set the wasActive for the first time
    this._wasActive = false;

    // Set the wasFolded
    this._wasFolded = this.folded;

    // Show the sidebar
    this._showSidebar();

    // Act on every media change
    this._matchMediaService.onMediaChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Get the active status from the service
        const isActive = this._matchMediaService.isActive(this.lockedOpen);

        // If the both status are the same, don't act
        if (this._wasActive === isActive) {
          return;
        }

        if (isActive) {
          this.isLockedOpen = true;
          this._showSidebar();
          this.opened = true;

          if (this._wasFolded) {
            this.folded = true;

            this._changeDetectorRef.markForCheck();
          }
        } else {
          this.isLockedOpen = false;
          this.unfold();
          this.opened = false;

          this._hideSidebar();
        }

        // Store the new active status
        this._wasActive = isActive;
      });
  }

  /**
   * Setup the initial folded status
   *
   * @private
   */
  private _setupFolded(): void {
    // Return, if sidebar is not folded
    if (!this.folded) {
      return;
    }

    // Return if the sidebar is closed
    if (!this.opened) {
      return;
    }

    // Programmatically add/remove padding to the element
    // that comes after or before based on the position
    let sibling = this._elementRef.nativeElement.nextElementSibling;

    const styleValue = this.foldedWidth + 'px';

    if (!sibling) {
      return;
    }

    this._foldAndSetSiblingsStyle(sibling, styleValue);
  }

  /**
   * Change some properties of the sidebar
   * and make it visible
   *
   * @private
   */
  private _showSidebar(): void {
    // Remove the box-shadow style
    this._renderer.removeStyle(this._elementRef.nativeElement, 'box-shadow');

    // Make the sidebar invisible
    this._renderer.removeStyle(this._elementRef.nativeElement, 'visibility');

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Change some properties of the sidebar
   * and make it invisible
   *
   * @private
   */
  private _hideSidebar(): void {
    // Add a delay so close animation can play
    setTimeout(() => {
      // Remove the box-shadow
      this._renderer.setStyle(
        this._elementRef.nativeElement,
        'box-shadow',
        'none'
      );

      // Make the sidebar invisible
      this._renderer.setStyle(
        this._elementRef.nativeElement,
        'visibility',
        'hidden'
      );
    }, 100);

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  private _foldAndSetSiblingsStyle(
    sibling: Element,
    styleValue: string,
    styleRule?: string
  ) {
    // Fold the sidebar
    this.fold();

    // Set the folded width
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'width',
      styleValue
    );
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'min-width',
      styleValue
    );
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'max-width',
      styleValue
    );

    // Set the style and class
    this._renderer.setStyle(sibling, styleRule || '', styleValue);
    this._renderer.addClass(this._elementRef.nativeElement, 'folded');
  }

  private _redirectDefaultLink(menuItems: NavigationMenuItem[]) {
    if (!this._shouldRedirect) {
      return;
    }

    this._shouldRedirect = false;

    if (
      this.router.url === '/' ||
      this.router.url === '/home' ||
      this.router.url === '/wcf?name=general-view-aui-wrapper&viewId=system:administration_home.159'
    ) {
        this.router.navigateByUrl('home');
        return;
    }
    
    const link = this._findMenuLink(menuItems);

    this.sidebarService.redirectLink$.next(link);
  }

  private _findMenuLink(menuItems: NavigationMenuItem[]): string | null {
    for (const index in menuItems) {
      const item = menuItems[index];

      if (item.link && item.visible) {
        return item.link;
      }

      const link = item.children ? this._findMenuLink(item.children) : null;

      if (link) {
        return link;
      }
    }

    return null;
  }

  private _getMenuItems() {
    this.sidebarService
      .getSidebarItems()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => { this.sidebarError = true; return of(undefined); })
      )
      .subscribe({
        next: (leftMenu: LeftNavigationMenu | undefined) => {
          if (!leftMenu) {
            return;
          }
          // Replace navigation menu item links from WCF to AUI based on Mapping
          this.sidebarService.replaceItemsLinkAsPerAUIMapping(leftMenu.menus ?? []);

          if (leftMenu.homepage) {
            this.sidebarService.replaceLinkAsPerAUIMapping(leftMenu.homepage);
          }

          const menuItems = leftMenu.menus;

          localStorage.setItem('homePage', JSON.stringify(leftMenu.homepage));
          this._redirectDefaultLink(menuItems ?? []);

          if (menuItems && menuItems.length > 0) {
            const uObj = menuItems.find((item) => item.id === 'u');
            if (uObj) {
              uObj.displayName = 'Other Users Dashboards';
              uObj.id = 'system:other-user-dashboards';
            }
            const user = menuItems.find((item) => item.id.startsWith('user:'));
            if (user) {
              user.id = 'system:other-user-dashboards';
            }
            this.sidebarService.updateNavItems(menuItems);
          }

          const bookmarksItem = leftMenu.bookmark;

          if (bookmarksItem?.children?.length) {
            this.sidebarService.updateBooksmarksItem(bookmarksItem);
          }
          if (leftMenu?.configurationItem?.children) {
            this.sidebarService.updateConfigItems(
              leftMenu.configurationItem.children
            );
          }
        },
        error: () => { this.sidebarError = true; },
        complete: () => {
          this.isLoading = false;
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  /**
   * Mouseenter
   */
  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.sidebarService.menuShouldBeFolded$.next(false);
  }

  /**
   * Mouseleave
   */
  @HostListener('mouseleave')
  onMouseLeave(): void {
    let isHoverMenuTooltip = false;

    this.sidebarService.isHover$.pipe(take(1)).subscribe(
      (data) => (isHoverMenuTooltip = data)
    );

    if (this.isHover || isHoverMenuTooltip) {
      this.isHover = false;
      isHoverMenuTooltip = false;
      this.sidebarService.updateHoverState(false);
    }

    this.sidebarService.menuShouldBeFolded$.next(true);
  }

  /**
   * Fold the sidebar permanently
   */
  fold(): void {
    // Only work if the sidebar is not folded
    if (this.folded) {
      return;
    }

    this.folded = true;
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Unfold the sidebar permanently
   */
  unfold(): void {
    // Only work if the sidebar is folded
    if (!this.folded) {
      return;
    }

    // Unfold
    this.folded = false;
    this.setSidebarWidthAccordingToSessionStorage();

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Toggle the sidebar fold/unfold permanently
   */
  toggleFold(): void {
    if (this.folded || this.sidebarStateFlag) {
      this.unfold();
    } else {
      this.fold();
    }
  }

  /**
   * Fold the temporarily unfolded sidebar back
   */
  foldTemporarily(): void {
    // Only work if the sidebar is folded
    if (!this.folded) {
      return;
    }

    // Fold the sidebar back
    this.unfolded = false;
    this.sidebarOverlay = false;

    // Set the folded width
    const styleValue = this.foldedWidth + 'px';

    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'width',
      styleValue
    );
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'min-width',
      styleValue
    );
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'max-width',
      styleValue
    );

    this.temporaryFold.emit();

    this.sidebarService.setFoldedState(true);
    const event = new CustomEvent('sidebarIsPinned', { detail: this.pinned });
    window.dispatchEvent(event);
    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Unfold the sidebar temporarily
   */
  unfoldTemporarily(): void {
    // Only work if the sidebar is folded
    if (!this.folded) {
      return;
    }

    // Unfold the sidebar temporarily
    this.unfolded = true;
    this.sidebarOverlay = true;

    // Remove the folded width
    this._renderer.removeStyle(this._elementRef.nativeElement, 'width');
    this._renderer.removeStyle(this._elementRef.nativeElement, 'min-width');
    this._renderer.removeStyle(this._elementRef.nativeElement, 'max-width');

    this.temporaryUnfold.emit();

    this.sidebarService.setFoldedState(false);

    this.setSidebarWidthAccordingToSessionStorage();
    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Change sidebar pinned state (ignore mouseleave if true)
   */
  onPinClick(): void {
    this.pinned = !this.pinned;
    this.sidebarOverlay = this.pinned ? false : true;
    sessionStorage.setItem('isPinned', '' + this.pinned);
    this.setSidebarState(this.pinned);
    this.sidebarService.pinnedState$.next(this.pinned);
    this.toggleFold();
    this.sidebarStateFlag = false;
  }

  onHover(): void {
    this.isHover = true;
  }

  onConfigure(): void {
    this.pinned = true;
    this.sidebarService.setEditState(true);
    this.folded = false;
  }

  onEditSaveAndCancel(): void {
    this.pinned = sessionStorage.getItem('isPinned') === 'true';
    this.folded = this.pinned ? false : true;
    this.setSidebarWidthAccordingToSessionStorage();
  }

  setSidebarState(isPinned: boolean): void {
    localStorage.setItem(this.LOCAL_STORAGE_SIDEBAR_STATE_KEY, isPinned.toString());
  }
}
